<template>
  <div class="pdf-preview" :style="wrapperStyle">
    <div class="toolbar">
      <div class="left">
        <a-button size="small" :disabled="loading" @click="reload">刷新</a-button>
        <a-button size="small" :disabled="loading || pageNum <= 1" @click="prevPage">
          上一页
        </a-button>
        <a-button size="small" :disabled="loading || pageNum >= totalPages" @click="nextPage">
          下一页
        </a-button>
        <span class="meta">{{ pageNum }} / {{ totalPages || 0 }}</span>
      </div>
      <div class="right">
        <a-button size="small" :disabled="loading" @click="zoomOut">-</a-button>
        <span class="meta">{{ Math.round(scale * 100) }}%</span>
        <a-button size="small" :disabled="loading" @click="zoomIn">+</a-button>
      </div>
    </div>

    <div class="body" ref="body">
      <div v-if="loading" class="status">PDF 加载中…</div>
      <div v-else-if="error" class="status error">
        {{ error }}
        <div class="hint">你可以尝试刷新，或检查该 PDF 链接是否允许跨域访问。</div>
      </div>
      <canvas v-show="!loading && !error" ref="canvas" class="canvas"></canvas>
    </div>
  </div>
</template>

<script>
const PDFJS_CDN_VERSION = '3.11.174'
const PDFJS_SRC = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${PDFJS_CDN_VERSION}/build/pdf.min.js`
const PDFJS_WORKER_SRC = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${PDFJS_CDN_VERSION}/build/pdf.worker.min.js`

function loadScriptOnce(src) {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[data-pdfjs-src="${src}"]`)
    if (existing) {
      if (existing.dataset.loaded === '1') return resolve()
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', () => reject(new Error(`加载失败: ${src}`)))
      return
    }

    const s = document.createElement('script')
    s.src = src
    s.async = true
    s.dataset.pdfjsSrc = src
    s.addEventListener('load', () => {
      s.dataset.loaded = '1'
      resolve()
    })
    s.addEventListener('error', () => reject(new Error(`加载失败: ${src}`)))
    document.head.appendChild(s)
  })
}

export default {
  props: {
    pdfUrl: {
      type: String,
      required: true,
    },
    height: {
      type: [Number, String],
      default: () => Math.round(window.innerHeight * 0.8),
    },
  },
  data() {
    return {
      loading: false,
      error: '',
      pageNum: 1,
      totalPages: 0,
      scale: 1,
      pdfDoc: null,
      renderTask: null,
      resizeObserver: null,
    }
  },
  computed: {
    wrapperStyle() {
      const h = typeof this.height === 'number' ? `${this.height}px` : this.height
      return { height: h }
    },
  },
  watch: {
    pdfUrl: {
      immediate: true,
      handler() {
        this.open()
      },
    },
  },
  mounted() {
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        if (!this.loading && !this.error && this.pdfDoc) this.renderPage()
      })
      if (this.$refs.body) this.resizeObserver.observe(this.$refs.body)
    } else {
      window.addEventListener('resize', this.onWindowResize, { passive: true })
    }
  },
  beforeDestroy() {
    this.cancelRender()
    this.closeDoc()
    if (this.resizeObserver) this.resizeObserver.disconnect()
    window.removeEventListener('resize', this.onWindowResize)
  },
  methods: {
    onWindowResize() {
      if (!this.loading && !this.error && this.pdfDoc) this.renderPage()
    },
    async ensurePdfjs() {
      await loadScriptOnce(PDFJS_SRC)
      if (!window.pdfjsLib) throw new Error('pdf.js 初始化失败')
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_SRC
      return window.pdfjsLib
    },
    cancelRender() {
      try {
        if (this.renderTask && this.renderTask.cancel) this.renderTask.cancel()
      } catch (e) {
        // ignore
      } finally {
        this.renderTask = null
      }
    },
    async closeDoc() {
      try {
        if (this.pdfDoc && this.pdfDoc.destroy) await this.pdfDoc.destroy()
      } catch (e) {
        // ignore
      } finally {
        this.pdfDoc = null
      }
    },
    async open() {
      const url = (this.pdfUrl || '').trim()
      this.error = ''
      this.totalPages = 0
      this.pageNum = 1

      if (!url) return

      this.loading = true
      this.cancelRender()
      await this.closeDoc()

      try {
        const pdfjsLib = await this.ensurePdfjs()
        const loadingTask = pdfjsLib.getDocument({
          url,
          withCredentials: false,
        })
        const doc = await loadingTask.promise
        this.pdfDoc = doc
        this.totalPages = doc.numPages || 0
        this.pageNum = 1
        await this.renderPage()
      } catch (e) {
        const msg = (e && e.message) || String(e)
        this.error = `PDF 预览失败：${msg}`
      } finally {
        this.loading = false
      }
    },
    async renderPage() {
      if (!this.pdfDoc) return
      const canvas = this.$refs.canvas
      const body = this.$refs.body
      if (!canvas || !body) return

      this.cancelRender()

      try {
        const page = await this.pdfDoc.getPage(this.pageNum)

        const baseViewport = page.getViewport({ scale: 1 })
        const maxWidth = Math.max(320, body.clientWidth - 24)
        const fitScale = maxWidth / baseViewport.width
        const finalScale = Math.max(0.2, Math.min(4, fitScale * this.scale))

        const viewport = page.getViewport({ scale: finalScale })
        const dpr = window.devicePixelRatio || 1

        const ctx = canvas.getContext('2d')
        canvas.width = Math.floor(viewport.width * dpr)
        canvas.height = Math.floor(viewport.height * dpr)
        canvas.style.width = `${Math.floor(viewport.width)}px`
        canvas.style.height = `${Math.floor(viewport.height)}px`

        ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
        ctx.clearRect(0, 0, viewport.width, viewport.height)

        this.renderTask = page.render({
          canvasContext: ctx,
          viewport,
        })
        await this.renderTask.promise
      } catch (e) {
        if (e && (e.name === 'RenderingCancelledException' || e.message === 'Rendering cancelled'))
          return
        const msg = (e && e.message) || String(e)
        this.error = `渲染失败：${msg}`
      } finally {
        this.renderTask = null
      }
    },
    async reload() {
      await this.open()
    },
    async prevPage() {
      if (this.pageNum <= 1) return
      this.pageNum -= 1
      await this.renderPage()
    },
    async nextPage() {
      if (this.pageNum >= this.totalPages) return
      this.pageNum += 1
      await this.renderPage()
    },
    async zoomIn() {
      this.scale = Math.min(3, Math.round((this.scale + 0.1) * 10) / 10)
      await this.renderPage()
    },
    async zoomOut() {
      this.scale = Math.max(0.3, Math.round((this.scale - 0.1) * 10) / 10)
      await this.renderPage()
    },
  },
}
</script>

<style lang="less" scoped>
.pdf-preview {
  width: 100%;
  display: flex;
  flex-direction: column;
  border: 1px solid #f0f0f0;
  background: #fff;
}

.toolbar {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 10px;
  border-bottom: 1px solid #f0f0f0;
  background: #fafafa;

  .left,
  .right {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  .meta {
    color: rgba(0, 0, 0, 0.65);
    font-size: 12px;
    white-space: nowrap;
  }
}

.body {
  flex: 1 1 auto;
  overflow: auto;
  padding: 12px;
  background: #f6f7f9;
  display: flex;
  justify-content: center;
  align-items: flex-start;
}

.canvas {
  background: #fff;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
}

.status {
  width: 100%;
  padding: 18px 14px;
  color: rgba(0, 0, 0, 0.65);
  background: #fff;
  border: 1px dashed #d9d9d9;
  border-radius: 6px;

  &.error {
    border-color: #ffccc7;
    color: #a8071a;
  }

  .hint {
    margin-top: 6px;
    color: rgba(0, 0, 0, 0.45);
    font-size: 12px;
  }
}
</style>
